import {
  ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output
} from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { Store } from '@ngrx/store';
import { flatMap as _flatMap, pullAt as _pullAt } from 'lodash-es';
import { finalize, map, takeUntil } from 'rxjs/operators';

import {
  AccountTurfInputModel
} from 'account/components/account-turf-input/account-turf-input.component';
import { selectCurrentUser } from 'app/_state/auth/auth-selectors';
import { getAssignableRoles } from 'auth/account-role-helper';
import { WithDisposableSubject } from 'core/mixins/with-disposable-subject';
import { ToastrService } from 'core/toastr.service';
import { validationToErrorMessages } from 'core/validators';
import { getSafe, isNilOrEmpty, uuid } from 'helpers';

import {
  CreateInvitationInput, CreateUserGQL, UserDetailsFragment, UserRole
} from 'generated/graphql';

// This object contains error messages which correspond with the form
// these are the error messages the correspond to the errors on the
// form control object.
const validationMessages = {
  firstName: {
    required: 'First name is required.'
  },
  lastName: {
    required: 'Last name is required.'
  },
  email: {
    required: 'Email address is required.'
  },
  role: {
    required: 'You must select a role.'
  }
};

export interface CreateInvitiationFormModel {
  email?: string;
  firstName?: string;
  lastName?: string;
  role?: any;
  linkedEntityId?: string;
  campaigns: Array<{
    campaign: {
      id: string;
      name: string;
    };
    assignment: AccountTurfInputModel;
    // UUID is used for the name field of the <ng-select> for the campaigns
    // this prevents issues when adding or removing campaigns because
    // each select needs a unique name, and using the index number
    // does not work when campaigns are popped due to the view LifeCycle
    uuid: string;
  }>;
}

@Component({
  selector: 'app-account-create',
  templateUrl: './account-create.component.html'
})
export class AccountCreateComponent extends WithDisposableSubject()
  implements OnInit, OnDestroy {
  ready = false;
  @Input() initialValues: CreateInvitiationFormModel;
  // Prevents redirect after save

  // Hides the button for adding another user
  @Input() hideAddAnother: boolean;
  @Output() userAdded = new EventEmitter<{ user: UserDetailsFragment; addAnother: boolean }>();

  // Model used in view, must go first!
  createInvitiationFormModel: CreateInvitiationFormModel;
  errorMessages: string[] = [];
  successMessages: string[] = [];
  // Roles that user is able to assign.
  roleOptions: Array<{ label: string; value: string }> = [];
  // Disables submit butting
  isSubmitting = false;
  // Keep track of which campaigns have been selected
  get selectedCampaignIds() {
    return this.createInvitiationFormModel?.campaigns?.map(c => c.campaign?.id).filter(i => i) || [];
  }

  constructor(
    private _cd: ChangeDetectorRef,
    private _toastrService: ToastrService,
    private _createUserGQL: CreateUserGQL,
    private _store: Store
  ) {
    super();
  }

  ngOnInit() {
    this._store.select(
      selectCurrentUser
    ).pipe(
      takeUntil(this.disposer),
      map(cu => getAssignableRoles(cu.role))
    ).subscribe((roleOptions) => {
      this.roleOptions = roleOptions.map((role) => ({ label: role, value: role }));
      this.createInvitiationFormModel = this.createFormModel();
      this.ready = true;
      this._cd.markForCheck();
    });
  }

  /**
   * Handles when user clicks "Add Another Campaign"
   * button
   */
  onClickAddCampaign() {
    this.createInvitiationFormModel.campaigns.push(
      {
        campaign: null,
        assignment: {
          tagIds: [],
          restrictionEnabled: this.createInvitiationFormModel.role === 'Activist',
        },
        uuid: uuid()
      });
  }

  /**
   * Handles when a campaign is selected.
   * Defaults limitAccess checkbox to true if user role is Activist;
   *
   * @param assignment Campaign assignment
   */
  onCampaignSelected(event: { id: string; name: string }, campaignAsssignment, f) {
    if (this.selectedCampaignIds.includes(event?.id)) {
      this._toastrService.warning('Campaign already selected.');
      return;
    }
    campaignAsssignment.campaign = event;
    campaignAsssignment.assignment.restrictionEnabled = this.createInvitiationFormModel.role === 'Activist';
  }

  /**
   * Handles when user clicks "Remove Campaign"
   * button
   *
   * @param campaignItem CampaignItem to remove
   */
  onClickRemoveCampaign(index) {
    _pullAt(this.createInvitiationFormModel.campaigns, index);
  }

  /**
   * Handles when submit invite is sent.
   *
   * @param form
   * @param redirectToList
   */
  sendInvite(form: UntypedFormGroup, addAnother = true) {
    this.isSubmitting = true;
    // Clear error messages
    this.errorMessages = [];
    if (form.invalid) {
      this.errorMessages = validationToErrorMessages(form, validationMessages);
      this.isSubmitting = false;
      return;
    }
    const input = this.prepareMutationData();

    this._createUserGQL.mutate({ input }).pipe(
      finalize(() => {
        this.isSubmitting = false;
        this._cd.markForCheck();
      }),
      map(({ data }) => data)
    )
      .subscribe(
        ({ createInvitation: { user } }) => {
          // Push to success messages
          this.successMessages.push(`Invite sent to ${user.email}`);
          // Reset the create invitation fields
          this.userAdded.emit({ user, addAnother });
          this.createInvitiationFormModel = this.createFormModel();
          form.reset();
        },
        (err) => {
          this.errorMessages = _flatMap(err.graphQLErrors, e => e.message);
          this._toastrService.error(err);
        }
      );
  }

  private prepareMutationData(): CreateInvitationInput {
    const turfAssignments: TurfAssignment[] = this.createInvitiationFormModel.campaigns
      .filter(c => c.campaign && c.assignment && !isNilOrEmpty(c.assignment))
      .filter(c => c.assignment)
      .map(c => {
        const role = this.createInvitiationFormModel.role;
        // Restriction can only be enabled on Leads and below.
        const isRestrictionEnabled = (role === 'Administrator' || role === 'Lead') ? false : c.assignment.restrictionEnabled;
        const turfAssignment: TurfAssignment = {
          campaign_id: c.campaign.id,
          tag_ids: getSafe(() => c.assignment.tagIds, []),
          restriction_enabled: isRestrictionEnabled
        };
        return turfAssignment;
      });

    const mutationData: CreateInvitationInput = {
      email: this.createInvitiationFormModel.email,
      firstName: this.createInvitiationFormModel.firstName,
      lastName: this.createInvitiationFormModel.lastName,
      role: this.createInvitiationFormModel.role,
      linkedEntityId: this.createInvitiationFormModel.linkedEntityId,
      campaigns: this.createInvitiationFormModel.campaigns.map(c => c.campaign?.id).filter(c => c),
      turfAssignments: JSON.stringify(turfAssignments)
    };

    return mutationData;
  }

  /**
   * Returns a blank form
   */
  private createFormModel(): CreateInvitiationFormModel {
    const defaults = this.initialValues || {} as CreateInvitiationFormModel;
    const form = {
      email: defaults.email || null,
      firstName: defaults.firstName || null,
      lastName: defaults.lastName || null,
      role: defaults.role || null,
      linkedEntityId: defaults.linkedEntityId,
      campaigns: defaults.campaigns || [
        {
          campaign: null,
          assignment: {
            tagIds: [],
            restrictionEnabled: getSafe(() => defaults.role, null) === UserRole.Activist,
          },
          uuid: uuid()
        }
      ]
    };
    return form;
  }

}
